import { transformPropsDeep, escapeHTML } from 'containers/App/utils';
import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get } from 'lodash';
import {
  mapQuestionsAndGroupsAsNodes,
  duplicateQuestion as duplicateQuestionHelper,
} from 'containers/QuestionnairePage/utils';
import { SURVEY_TYPE_PEEQ } from 'containers/Survey/constants';

import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
const brandPerceptionsGenerator = ({ template, variables, surveyType }) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  const Survey = new TemplateSurveyState({
    // fetchedQuestionnaire: template.questionnaireTemplate,
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  // extract and prepare user variables
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const yourBrand = get(find(variables, ['name', 'your_brand']), 'value', {});
  const competitors = get(
    find(variables, ['name', 'competitors']),
    'value',
    [],
  );
  const attributes = get(find(variables, ['name', 'attributes']), 'value', []);
  const benefits = get(find(variables, ['name', 'benefits']), 'value', []);
  const attitudes = get(find(variables, ['name', 'attitudes']), 'value', []);
  const defaultRecurrence = get(
    find(variables, ['name', 'defaultRecurrence']),
    'value',
    undefined,
  );

  // update texts in the template
  // use simplest / less performant option
  // of doing stringify, text replace, parse
  const replaceProductDescription = (value) =>
    value.replace(
      /\{\{product_category\}\}/gi,
      escapeHTML(productCategory.text),
    );
  const replaceBrandText = (value) =>
    value.replace(/\{\{your_brand\}\}/gi, escapeHTML(yourBrand.text));

  const templateWithProductDescription = transformPropsDeep(
    template.questionnaireTemplate,
    {
      prompt: replaceProductDescription,
      htmlPrompt: replaceProductDescription,
    },
  );

  const { nodes: brandPerceptionsTemplateQuestions } =
    mapQuestionsAndGroupsAsNodes(templateWithProductDescription);

  const initialQuestions = brandPerceptionsTemplateQuestions.slice(0, 4);
  const initialGroup = brandPerceptionsTemplateQuestions[4];

  /*
    Prepare Template questions
  */
  const brandAndCompetitorAnswers = [
    {
      type: 0,
      body: yourBrand.text,
      media: yourBrand.media,
      refId: `answer-ref-brand`,
    },
    ...competitors.map((competitor, competitorIndex) => ({
      type: 0,
      body: competitor.text,
      media: competitor.media,
      refId: `answer-ref-${competitorIndex}`,
    })),
    {
      type: 2,
      body: 'None of the above',
      refId: `answer-ref-none`,
    },
  ];

  const reflectionAttributeAnswers = [
    ...attributes.map((attribute, attributeIndex) => ({
      type: 0,
      body: attribute,
      refId: `answer-ref-${attributeIndex}`,
    })),
    ...benefits.map((benefit, benefitIndex) => ({
      type: 0,
      body: benefit,
      refId: `answer-ref-${benefitIndex}`,
    })),
    ...attitudes.map((attitude, attitudeIndex) => ({
      type: 0,
      body: attitude,
      refId: `answer-ref-${attitudeIndex}`,
    })),
  ];

  // add answers (brand + competitors)
  Object.assign(initialQuestions[0], { media: productCategory.media });
  Object.assign(initialQuestions[1], {
    answers: brandAndCompetitorAnswers,
    media: productCategory.media,
  });
  // remove CF answers (they will be automatically added in 2nd pass)
  Object.assign(initialQuestions[2], {
    answers: [],
    media: productCategory.media,
  });
  Object.assign(initialQuestions[3], {
    answers: [],
  });

  const initialQuestionDuplicates = initialQuestions.map((question) =>
    duplicateQuestionHelper(question),
  );

  // remove CF answers (they will be automatically added in 2nd pass)
  Object.assign(initialQuestions[2], { answers: [] });
  Object.assign(initialQuestions[3], { answers: [] });

  // edit correct CF
  initialQuestionDuplicates[2].carryForward[0].questionRefId =
    initialQuestionDuplicates[1].refId;
  initialQuestionDuplicates[3].carryForward[0].questionRefId =
    initialQuestionDuplicates[1].refId;

  initialQuestionDuplicates.forEach((question) =>
    Survey.questionnaire.addQuestion(question),
  );
  // trigger 2nd pass to fix CF answers
  Survey.questionnaire.updateQuestion(initialQuestionDuplicates[1]);

  // repeat initialGroup for brand and as many competitors
  Object.assign(initialGroup.questions[1], {
    answers: reflectionAttributeAnswers,
  });

  const brandGroup = transformPropsDeep(initialGroup, {
    prompt: replaceBrandText,
    htmlPrompt: replaceBrandText,
  });
  brandGroup.questions.forEach((question) => {
    Object.assign(question, { media: yourBrand.media });
  });
  const groupQuestionDuplicates = [
    duplicateQuestionHelper(brandGroup), // this is for "our brand"
    ...competitors.map((competitor) => {
      const replaceCompetitor = (value) =>
        value.replace(/\{\{your_brand\}\}/gi, escapeHTML(competitor.text));
      const competitorGroup = transformPropsDeep(initialGroup, {
        prompt: replaceCompetitor,
        htmlPrompt: replaceCompetitor,
      });
      competitorGroup.questions.forEach((question) => {
        Object.assign(question, { media: competitor.media });
      });
      return duplicateQuestionHelper(competitorGroup);
    }), // this is for the competitor brands
  ];

  groupQuestionDuplicates.forEach((groupQuestion) => {
    Survey.questionnaire.addQuestion(groupQuestion);
  });

  // q2 add logic to answer NOTA to redirect to end of survey
  Survey.questionnaire.linkAnswerToQuestion({
    question: initialQuestionDuplicates[1],
    answerRefId: initialQuestionDuplicates[1].answers.find(
      ({ type }) => type === 2,
    ).refId,
    targetQuestion: { refId: END_OF_SURVEY },
    context: { questionId: initialQuestionDuplicates[1].refId },
  });

  // q4 add logic to answers for initial redirect to groups (based on q2 answers)
  initialQuestionDuplicates[1].answers.forEach((answer, answerIndex) => {
    if (answer.type === 0) {
      // normal answers (exclude the NOTA)
      Survey.questionnaire.linkAnswerToQuestion({
        question: initialQuestionDuplicates[3],
        answerRefId: answer.refId,
        targetQuestion: groupQuestionDuplicates[answerIndex],
        context: { questionId: initialQuestionDuplicates[1].refId },
      });
    }
  });

  // add group exit logic to go to next group if selected in Q2
  groupQuestionDuplicates.forEach((groupQuestion, groupIndex) => {
    initialQuestionDuplicates[1].answers.forEach((answer, answerIndex) => {
      if (answerIndex > groupIndex && answer.type === 0) {
        Survey.questionnaire.linkAnswerToQuestion({
          question: groupQuestion,
          answerRefId: answer.refId,
          targetQuestion: groupQuestionDuplicates[answerIndex],
          context: { questionId: initialQuestionDuplicates[1].refId },
        });
      }
    });

    Survey.questionnaire.setQuestionDefaultOutcome(groupQuestion, {
      refId: END_OF_SURVEY,
    });
  });
  // add product_category and media to all group questions

  return { state: Survey.state, defaultRecurrence };
};

export default brandPerceptionsGenerator;
